import useDebounce from "@/hooks/useDebounce";
import { CircularProgress, FormControl } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ChangeEvent, FC, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "../../../index.css";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, X } from "lucide-react";
import { listCountryDiscountService } from "@/services/discount.service";
import { QUERY_KEY } from "@/constants";

interface IProps {
  value?: string;
  onChange: (value: string) => void;
}

const SelectCountry: FC<IProps> = ({ onChange, value }) => {
  const [open, setOpen] = useState(false);
  const [textSearch, setTextSearch] = useState<string>("");
  const query = useDebounce(textSearch, 300);

  const { data: listCountry, isLoading } = useQuery({
    queryKey: [QUERY_KEY.listCountry],
    queryFn: () => listCountryDiscountService(),
  });

  const filteredCountries =
    listCountry?.countries &&
    listCountry?.countries
      ?.filter((country) =>
        country.countryName.toLowerCase().includes(query.toLowerCase()),
      )
      .sort((a, b) => a.countryName.localeCompare(b.countryName));

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setTextSearch(event.target.value);
  };

  const handleSelectCountry = (countryCode: string) => {
    onChange(countryCode);
    setOpen(false);
  };

  const renderValue = () => {
    const selectedCountry =
      listCountry?.countries &&
      listCountry?.countries?.find((country) => country.countryCode === value);

    return (
      <div className="mt-1 flex flex-wrap items-center gap-2 py-1">
        {isLoading && (
          <div className="flex items-center justify-center py-2">
            <CircularProgress size="12px" color="inherit" />
          </div>
        )}
        {selectedCountry ? (
          <div className="flex max-w-full items-center gap-1 rounded-[4px] bg-[#E0E7FF] px-2 py-1">
            <p className="line-clamp-1 text-wrap text-[10px] font-medium leading-4 text-[#322F80]">
              {selectedCountry.countryName}
            </p>
            <div
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleSelectCountry("");
              }}
            >
              <X className="size-3 text-[#322F80]" />
            </div>
          </div>
        ) : (
          <span className="text-sm text-text">Select country</span>
        )}
      </div>
    );
  };

  return (
    <FormControl className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="flex cursor-pointer items-center justify-between rounded-lg border border-[#E2E8F0] bg-white px-4 py-3">
            <div>
              {value ? (
                renderValue()
              ) : (
                <span className="text-sm text-text">Select country</span>
              )}
            </div>
            <KeyboardArrowDownIcon
              className=""
              sx={{
                color: "#94A3B8",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="w-full"
          style={{
            width: "var(--radix-popover-trigger-width)",
          }}
        >
          <div className="flex flex-col gap-2">
            <input
              type="text"
              placeholder="Search countries..."
              value={textSearch}
              onChange={handleSearch}
              className={cn(
                "placeholder:text-[#94A3B8], h-12 rounded-lg border border-[#E2E8F0] bg-white px-3 text-[#020617] outline-none placeholder:text-sm placeholder:font-normal",
              )}
            />
            <div className="flex max-h-[270px] flex-col overflow-y-auto">
              {isLoading && (
                <div className="flex items-center justify-center py-2">
                  <CircularProgress size="22px" color="inherit" />
                </div>
              )}

              {filteredCountries?.map((country) => (
                <div
                  key={country.countryId}
                  className="flex cursor-pointer items-center rounded-md p-2 hover:bg-zinc-100"
                  onClick={() => {
                    handleSelectCountry(country.countryCode);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === country.countryCode
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <span className="text-sm text-[#475569]">
                    {country.countryName}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </FormControl>
  );
};

export default SelectCountry;
